import React, { useEffect, useState } from 'react';
import { Spinner } from '@myhealth-raccoon/components';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { captureException } from '@sentry/react';

import { useDispatch, useSelector } from 'src/store';
import { bookingService, BookingUserProfileModel } from 'src/services/booking';
import { AppointmentType } from 'src/services/availability';
import { appointmentSlice } from 'src/store/appointment';
import { isRaccoonError } from '@myhealth-raccoon/services';
import { ContentContainer } from 'src/components/ContentContainer';
import { isRequestError } from 'src/utils/requestError';

// Perform Patient/Doctor/Appttype Conflic checking
function PerformConflictChecks(
    { appointmentType, bannedLevel, doctor, isNewPatient, lastF2FDate, lastSeenDid, patientDOB }: {
        isNewPatient: boolean,
        patientDOB: string,
        lastSeenDid?: number,
        lastF2FDate?: string,
        bannedLevel?: string,
        doctor?: BookingUserProfileModel,
        appointmentType?: AppointmentType
    }
) {
    let conflict = { valid: true, conflictId: 0, reason: '' };
    const lastF2FVisitInMonths = appointmentType?.lastF2FVisitInMonths !== undefined ? parseInt(appointmentType.lastF2FVisitInMonths) : NaN;
    const lowAgeMin = appointmentType?.lowAgeMin ? parseInt(appointmentType.lowAgeMin) : NaN;
    const lowAgeMax = appointmentType?.lowAgeMax ? parseInt(appointmentType.lowAgeMax) : NaN;
    const upperAgeMin = appointmentType?.upperAgeMin ? parseInt(appointmentType.upperAgeMin) : NaN;
    const upperAgeMax = appointmentType?.upperAgeMax ? parseInt(appointmentType.upperAgeMax) : NaN;
    let patientAge = -1;
    try {
        patientAge = moment().diff(moment(patientDOB.toString()), "years");
    } catch (_) {
        patientAge = -1;
    }

    // 1: no new patient for Dr.
    if (isNewPatient && (doctor?.noNewPatient || doctor?.myPatient)) {
        conflict = { valid: false, conflictId: 1, reason: 'The selected doctor is not seeing new patients to the clinic.' };
    }
    // 2: dr will only see is own patients
    else if (doctor?.myPatient && (lastSeenDid === undefined || lastSeenDid.toString() !== doctor.bpUserId)) {
        conflict = { valid: false, conflictId: 2, reason: 'The selected doctor does is not accepting any new patients.' };
    }
    // 3: last face-to-face visit too long ago
    else if (lastF2FVisitInMonths > 0 &&
        (!lastF2FDate
            || (lastF2FDate && moment().diff(moment(lastF2FDate.toString()).format('YYYY-MM-DD'), "months") >= lastF2FVisitInMonths)
        )) {
        conflict = { valid: false, conflictId: 3, reason: 'Patients last Face to Face visit has been too long ago for this type of Appointment.' };
    }
    // 4: Appintment Type is not appropriate for Patient's Age
    else if (
        (lowAgeMin && patientAge < lowAgeMin)
        || (lowAgeMax && patientAge > lowAgeMax)
        || (upperAgeMin && patientAge < upperAgeMin)
        || (upperAgeMax && patientAge > upperAgeMax)
    ) {
        conflict = { valid: false, conflictId: 4, reason: 'Appintment Type is not appropriate for Patient Age.' };
    }
    // 5: Banned Patient - Note, banned patients will be caught as a patient verification exception error
    else if (bannedLevel && (bannedLevel.includes("Level 1") || bannedLevel.includes("Level 2"))) {
        conflict = { valid: false, conflictId: 5, reason: 'Appointments can not be made.' };
    }
    return conflict;
}

export function CreateBookingPassThrough() {
    const navigate = useNavigate();

    const [error, setError] = useState<Error>();

    const dispatch = useDispatch();
    const {
        clinicId, dateTime, appointmentTypeId, isNewPatient,
        timeSlot,
        doctor: { data: doctor },
        appointmentType: { data: appointmentType }
    } = useSelector(state => state.appointment);

    const {
        patientDOB,
        patientName,
        verification
    } = useSelector(state => state.bookingFlow);

    useEffect(() => {
        (async () => {
            const targetDate = moment(dateTime);
            const conflict = PerformConflictChecks({
                doctor,
                appointmentType,
                isNewPatient,
                patientDOB,
                lastSeenDid: verification?.lastSeenDid,
                lastF2FDate: verification?.lastF2FDate,
                bannedLevel: verification?.bannedLevel
            });
            // On Conflict Navigation
            if (!conflict.valid) {
                switch (conflict.conflictId) {
                    case 1:
                    case 2:
                        navigate('/app/err-provider-no-new-patients', { replace: true });
                        break;

                    case 3:
                        navigate('/app/err-appttype-requires-f2f', { replace: true, state: { lastF2FVisitInMonths: appointmentType?.lastF2FVisitInMonths } });
                        break;

                    case 4:
                        navigate('/app/err-appttype-not-available', { replace: true });
                        break;

                    case 5:
                        navigate('/app/err-unable-sched-appt', { replace: true, state: { bannedLevel: verification?.bannedLevel } });
                        break;
                }
                return;
            }

            // Need to Check and confirm - appointmentType.apptQuestionInBase64 / continueOrNo

            try {
                const reservedBooking = await bookingService.CreateBooking(clinicId, {
                    AppointmentTypeId: Number(appointmentTypeId),
                    Date: targetDate.format("YYYY-MM-DD"),
                    AutomedTime: timeSlot,
                    Duration: Number(appointmentType!.standardDurationInMins),
                    PatientName: patientName,
                    BPUserId: Number(doctor!.bpUserId),
                    AppointmentComment: ""
                });
                dispatch(appointmentSlice.actions.setReservedBooking(reservedBooking));

                navigate('/app/additional-details', { replace: true });
            } catch (e) {
                if (e instanceof Error) {
                    // IF return reason = 1, then Booking slot was taken. 
                    if (isRequestError(e) && isRaccoonError(e.data) && e.data.reason === 1) {
                        navigate('/app/unavailable-slottime', { replace: true });
                        return;
                    }
                    captureException(e)
                    setError(e);
                }
            }
        })();
    }, [appointmentTypeId, clinicId, dateTime, dispatch, doctor, appointmentType, navigate, isNewPatient, patientDOB, verification, patientName, timeSlot]);

    return <>
        <ContentContainer>
            <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Please wait</h1>
            {error
                ? <pre className='text-error whitespace-pre-wrap'>
                    {error.name}: {error.message}
                </pre>
                : <Spinner className='block mx-auto w-fit' />
            }
        </ContentContainer>
    </>
}